import { navigate, useLocation } from '@reach/router'
import { TabContent, TabHeader, Tabs, TabsContents, TabsHeaders } from '@vp/visage'
import classNames from 'classnames'
import * as React from 'react'
// import { GatsbyTypes.ContentfulTab, GatsbyTypes.Maybe } from '../../types/graphql-types'
import WidgetTocSection from '../widget-toc-section/widget-toc-section.component'
import styleClasses from './widget-tabs.module.scss'

export type WidgetTabsProps = {
  tabs?: GatsbyTypes.Maybe<readonly GatsbyTypes.Maybe<GatsbyTypes.tabFragment>[]>
}

function getByTitle(tabs?: WidgetTabsProps['tabs'], title?: string | null) {
  const query = title ? title.toLowerCase() : ''
  return tabs?.find(tab => tab?.title?.toLowerCase() === query)
}

function getDefaultTabId(tabs?: WidgetTabsProps['tabs']) {
  // try using localstorage
  try {
    if (typeof localStorage !== 'undefined') {
      const selectedTabTitle = localStorage.getItem('selectedTab')
      const selectedTab = getByTitle(tabs, selectedTabTitle)
      if (selectedTab) {
        return selectedTab.id
      }
    }
  } catch (e) {
    console.error(e)
  }
  // fallback to first tab
  if (Array.isArray(tabs) && tabs.length && tabs[0]?.id) {
    return tabs[0].id
  }
  // nothing to return
  return undefined
}

function useTabId(tabs?: WidgetTabsProps['tabs']) {
  const location = useLocation()
  const [tabId, setTabId] = React.useState<string | undefined>(() => getDefaultTabId(tabs))
  React.useEffect(() => {
    try {
      const queryParams = new URLSearchParams(location.search)
      const queryParamTabTitle = queryParams.get('tab')
      const selectedTab = getByTitle(tabs, queryParamTabTitle)
      if (selectedTab) {
        setTabId(selectedTab.id)
      } else {
        const defaultTabId = getDefaultTabId(tabs)
        setTabId(defaultTabId)
      }
    } catch (e) {
      console.error(e)
    }
  }, [tabs, location.search])

  return tabId
}

const WidgetTabs: React.FC<WidgetTabsProps> = ({ tabs }) => {
  const tabId = useTabId(tabs)
  if (!tabs || !tabs?.length) return null
  return (
    <Tabs
      className={classNames(styleClasses.tabs)}
      selectedTabId={tabId}
      onRequestTabChange={newTabId => {
        const selectedTab = tabs?.find(tab => tab?.id === newTabId)
        if (selectedTab && selectedTab.title) {
          navigate(`?tab=${selectedTab.title}`)
        }
      }}
    >
      <TabsHeaders className={classNames('theme-aware', styleClasses.tabsHeader)}>
        {tabs.map(tab =>
          tab ? (
            <TabHeader key={tab.id} tabId={tab.id}>
              {tab.title}
            </TabHeader>
          ) : null,
        )}
      </TabsHeaders>
      <TabsContents className="theme-aware">
        {tabs.map(tab =>
          tab ? (
            <TabContent key={tab.id} tabId={tab.id}>
              <WidgetTocSection nodes={tab.sections} renderToc={tab.generateSectionToc} tocHeading={tab.title} />
            </TabContent>
          ) : null,
        )}
      </TabsContents>
    </Tabs>
  )
}

export default WidgetTabs
